/* eslint-disable */
import _ from 'lodash';
import iplib from 'ipaddr.js';
import BigInteger from 'big-integer';
import { Address4, Address6 } from 'ip-address';

function ipAddressHelper() {
    const IPV4 = 'v4';
    const IPV6 = 'v6';

    const IPV4_MAX_SUBNET_MASK = 32;
    const IPV6_MAX_SUBNET_MASK = 128;

    // the API
    return {
        isValid,
        areValid,
        isV4,
        isV6,
        isSubnet,
        compareTo,
        areSameVersion,
        getVersion,
        getAddress,
        getSubnetMask,
        getFullAddress,
        getRangeBigIntegers,
        parse,
        removeSubnet,
        isIPAddress,
        isIPv4Address,
        isIPv6Address,
        validateSubnet,
        isAddressMaxSubnet,
        encompasses,
        rangeEncompasses,
        normalizeIpAddress,
        formatIPAddress,
        formatIPv6,
        validateDomainName,
    };

    /**
     * Compares two IP addresses. If `ipA` is less than `ipB`, this method
     * returns -1. If they are the same this method returns 0. If `ipB` is greater
     * than `ipA`, this method returns 1.
     *
     * Note, both IP addresses must be valid and the same type. If one is invalid
     * or of a different type, this method returns `undefined`.
     *
     * @param {String/Address4/Address6} ipA
     * @param {String/Address4/Address6} ipB
     * @return {Number}
     */
    function compareTo(ipA, ipB) {
        ipA = parse(ipA);
        ipB = parse(ipB);

        if (areSameVersion(ipA, ipB)) {
            const lhs = ipA.bigInteger();
            const rhs = ipB.bigInteger();
            const cmpValue = lhs.compareTo(rhs);

            // normalize the value to be just -1, 1, and 0
            if (cmpValue < 0) {
                return -1;
            }

            if (cmpValue > 0) {
                return 1;
            }

            return 0;
        }
    }

    /**
     * Tests to see if the provided IPs are all the same version. If any of the
     * IPs provided are invalid, this method returns `false`.
     *
     * @param {...String|Address4|Address6} ips
     * @return {Boolean}
     */
    function areSameVersion(...ipStrings) {
        const ips = _.map(ipStrings, parse);
        let version;

        for (let i = 0; i < ips.length; i++) {
            const ip = ips[i];
            const ipVersion = getVersion(ip);

            if (!ip) {
                return false;
            }

            if (!version) {
                version = ipVersion;
            } else if (ipVersion !== version) {
                return false;
            }
        }

        return true;
    }

    /**
     * Tests to see if the given IPs are all valid
     *
     * @param {...String} ipStrings
     * @return {Boolean}
     */
    function areValid(...ipStrings) {
        const ips = _.map(ipStrings, parse);

        for (let i = 0; i < ips.length; i++) {
            const ip = ips[i];

            if (!isValid(ip)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Tests if an IP address is valid
     *
     * @param {Object/Address4/Address6} ipString
     * @return {Boolean}
     */
    function isValid(ipString) {
        return !!parse(ipString);
    }

    /**
     * Tests if a given IP address is an IPv4 address
     *
     * @param {String/Address4/Address6} ipString
     * @return {Boolean}
     */
    function isV4(ipString) {
        const ip = parse(ipString);

        return !_.isNil(ip) && ip.v4;
    }

    /**
     * Tests if a given IP address is a IPv6 address
     *
     * @param {String/Address4/Address6} ipString
     * @return {Boolean}
     */
    function isV6(ipString) {
        const ip = parse(ipString);

        return !_.isNil(ip) && !ip.v4;
    }

    /**
     * Returns the type of IP address.
     *
     * @param {String} ip
     * @return {String/Boolean} `false` if invalid, otherwise "v4" or "v6".
     */
    function getType(ip) {
        let type = isV4(ip) ? IPV4 : false;
        if (!type) type = isV6(ip) ? IPV6 : false;
        if (!type) return false;

        return type;
    }

    /**
     * Returns 4 or 6 (or `undefined`) based on the version of an IP
     * address
     *
     * @param {String/Address4/Address6} ipString
     * @return {Number}
     */
    function getVersion(ipString) {
        const ip = parse(ipString);

        if (isV4(ip)) {
            return 4;
        }

        if (isV6(ip)) {
            return 6;
        }
    }

    /**
     * Tests if a given IP address is configured to be a subnet.
     * For IPv4, this means the subnet mask is less than 32. For IPv6,
     * the subnet mask must be less than 128.
     *
     * @param {String/Address4/Address6} ipString
     * @return {Boolean}
     */
    function isSubnet(ipString) {
        const ip = parse(ipString);
        if (!ip) return false;

        // get the ip type and return if its not valid
        const type = getType(ip);
        if (!type) return false;

        // note ip-address will add a /32 if it's a specific ip
        // (e.g.) 1.0.0.0 will be 1.0.0.0/32 so we need to determine if the ipString has a / in it
        if (ipString.indexOf('/') < 0) {
            return false;
        }

        // test the subnet based on the type
        return isSubnetValid(ip.subnetMask, type);
    }

    /**
     * Given an IP address, this function will return it without the subnet. It does not do any
     * modifications to the IP address itself, such as changing it to the start or end address.
     *
     * @param {String} ipString
     * @return {String}
     */
    function removeSubnet(ipString) {
        if (isValid(ipString)) {
            return _getAddressWithoutSubnet(ipString);
        }
    }

    /**
     * Returns only the IP address portion of a given IP address. This
     * would be used to exclude the CIDR mask.
     *
     * @param {String/Address4/Address6} ipString
     * @return {String}
     */
    function getAddress(ipString) {
        try {
            return _getAddressWithoutSubnet(ipString);
        } catch (e) {
            // Nothing to do
        }
    }

    /**
     * Checks to see if a subnet is valid for a given IP address type
     *
     * @param {String} subnet
     * @param {String} type Either "v4" or "v6"
     * @return {Boolean}
     */
    function isSubnetValid(subnet, type) {
        if (type === IPV4) return subnet <= IPV4_MAX_SUBNET_MASK;
        if (type === IPV6) return subnet <= IPV6_MAX_SUBNET_MASK;
        return false;
    }

    /**
     * Tests whether or not the address provided is the maximum possible subnet
     *
     * For example:
     *
     *     IpAddressHelper.isAddressMaxSubnet('192.168.0.1/32') // true
     *     IpAddressHelper.isAddressMaxSubnet('192.168.0.0/24') // false
     *
     * @param {String} ipAddr
     * @return {Boolean} `true` if the max subnet has been reached. `false` if not. `undefined` if ipAddr is invalid
     */
    function isAddressMaxSubnet(ipAddr) {
        const ip = parse(ipAddr);

        if (ip) {
            const sub = getSubnetMask(ip);

            return (isV4(ip) && sub === IPV4_MAX_SUBNET_MASK) || (isV6(ip) && sub === IPV6_MAX_SUBNET_MASK);
        }
    }

    /**
     * Validates that a subnet is valid for the type of IP address. This
     * works for both IPv4 and IPv6 addresses
     *
     * For example
     *
     *     IpAddressHelper.validateSubnet('192.168.0.0', 32) // true
     *     IpAddressHelper.validateSubnet('192.168.0.0', 33) // false
     *
     * @param {String|Address4|Address6} ip
     * @param {Number} [subnet=ip.subnet]
     * @return {Boolean}
     */
    function validateSubnet(ip) {
        ip = parse(ip);

        if (isV4(ip)) {
            return getSubnetMask(ip) <= IPV4_MAX_SUBNET_MASK;
        }

        if (isV6(ip)) {
            return getSubnetMask(ip) <= IPV4_MAX_SUBNET_MASK;
        }

        return false;
    }

    /**
     * Checks to see if an IP address is a specific address, without a subnet.
     * This method works with both IPv4 and IPv6.
     *
     * Note, if a subnet is included (even /32 or /128), this method will fail. The IP
     * address is assumed not to be in CIDR form.
     *
     * @param {String} ipString
     * @return {Boolean}
     */
    function isIPAddress(ipString) {
        return isIPv4Address(ipString) || isIPv6Address(ipString);
    }

    /**
     * Checks to see if an IP address is a specific address, without a subnet.
     * This method only validates IPv4 addresses. IPv6 will return false.
     *
     * Note, if a subnet is included (even /32 or /128), this method will fail. The IP
     * address is assumed not to be in CIDR form.
     *
     * @param {String} ipString
     * @return {Boolean}
     */
    function isIPv4Address(ipString) {
        const ip = parse(ipString);

        return isV4(ip) && !ip.parsedSubnet;
    }

    /**
     * Checks to see if an IP address is a specific address, without a subnet.
     * This method only validates IPv6 addresses. IPv4 will return false.
     *
     * Note, if a subnet is included (even /32 or /128), this method will fail. The IP
     * address is assumed not to be in CIDR form.
     *
     * @param {String} ipString
     * @return {Boolean}
     */
    function isIPv6Address(ipString) {
        const ip = parse(ipString);

        return isV6(ip) && !ip.parsedSubnet;
    }

    /**
     * Returns the subnet mask of a given IP address. This will always return
     * a value, even if not present in the IP address
     *
     * @param {String/Address4/Address6} ipString
     * @return {Number} The subnet mask
     */
    function getSubnetMask(ipString) {
        try {
            return parse(ipString).subnetMask;
        } catch (e) {
            // Nothing to do
        }
    }

    /**
     * Returns the IP address and subnet mask for a given IP address
     *
     * @param {String/Address4/Address6} ipString
     * @return {String}
     */
    function getFullAddress(ipString) {
        const ip = parse(ipString);

        if (ip) {
            return `${_getAddressWithoutSubnet(ip)}/${_getAddressSubnet(ip)}`;
        }
    }

    /**
     * Parses an IP address from a string and returns an instance of `Address4` or `Address6`
     *
     * @param {String/Address4/Address6} ipString
     * @return {Address4/Address6} or `undefined` if invalid
     */
    function parse(ipString) {
        // to parse an IPv4 or IPv6 type
        return fromType(ipString, Address4) || fromType(ipString, Address6);
    }

    /**
     * @private
     *
     * Creates an IP address from a specific type. If there is a parse error or the
     * IP is not valid, this method returns nothing
     *
     * @param {String} ipString The IP address string
     * @param {Function} T A constructor for an IP type (Address4 or Address 6)
     * @return {Object} A parsed IP or `undefined`
     */
    function fromType(ipString, T) {
        if (_.isObject(ipString) && ipString instanceof T) {
            return ipString;
        }

        try {
            const ip = new T(ipString);

            if (ip) {
                return ip;
            }
        } catch (e) {
            // Nothing to do
        }
    }

    /**
     * This function returns whether or not `rhs` encompasses `lhs`.
     *
     * For this to return true, `rhs` must be smaller than `lhs`.
     *
     * For example:
     *
     *     IpAddressHelper.encompasses('192.168.0.1/32', '0.0.0.0/0') // true
     *     IpAddressHelper.encompasses('0.0.0.0/0', '192.168.0.1/32') // false
     *
     * @param {String|Address4|Address6} child
     * @param {String|Address4|Address6} parent
     * @return {Boolean} Or `undefined` if anything is invalid
     */
    function encompasses(child, parent) {
        const childRange = getRangeBigIntegers(child);
        const parentRange = getRangeBigIntegers(parent);
        return rangeEncompasses(childRange, parentRange);
    }

    function rangeEncompasses(childRange, parentRange) {
        if (!childRange || !parentRange || childRange.version !== parentRange.version) {
            return false;
        }

        return parentRange.start.compare(childRange.start) <= 0 && parentRange.end.compare(childRange.end) >= 0;
    }

    function getRangeBigIntegers(subnet) {
        subnet = parse(subnet);
        return {
            version: getVersion(subnet),
            start: _getStartAddressBigInteger(subnet),
            end: _getEndAddressBigInteger(subnet),
        };
    }

    /**
     * @private
     *
     * Returns a BigInteger representing the start address for a given subnet
     *
     * @param {Address4|Address6} address
     * @return {BigInteger}
     */
    function _getStartAddressBigInteger(address) {
        const addrInt = _toBigInteger(address);
        const maskBits = _getAddressSubnet(address);
        const totalBits = _getAddressBits(address);
        const shiftCount = totalBits - maskBits;

        // start address is easy. Wipe out total-mask bits on LSB side
        return addrInt.shiftRight(shiftCount).shiftLeft(shiftCount);
    }

    /**
     * @private
     *
     * Returns a BigInteger representing the ending address for a given subnet
     *
     * @param {Address4|Address6} address
     * @return {BigInteger}
     */
    function _getEndAddressBigInteger(address) {
        const addrInt = _toBigInteger(address);
        const mask = _getSubnetMaskBigInteger(address);

        return addrInt.or(mask);
    }

    /**
     * @private
     *
     * Returns the subnet mask as a BigInteger. Xor it with -1 to flip the bits
     *
     * For example:
     *
     *     _getSubnetMaskBigInteger("192.168.0.0/24") // => BigInteger("00000000000000000000000011111111", 2) // aka the number 255
     *
     * @param {Address4|Address6} address
     * @return {BigInteger}
     */
    function _getSubnetMaskBigInteger(address) {
        const mask = _getAddressSubnet(address);
        const maskBits = _getAddressBits(address);
        const maskStr = `${_.repeat('0', mask)}${_.repeat('1', maskBits - mask)}`;

        return BigInteger(maskStr, 2);
    }

    /**
     * @private
     *
     * Returns the number of bits in a given address. 32 for IPv4, 128 for IPv6.
     *
     * @param {Address4|Address6} address
     * @return {Number}
     */
    function _getAddressBits(address) {
        if (isV4(address)) {
            return IPV4_MAX_SUBNET_MASK;
        }

        return isV6(address) ? IPV6_MAX_SUBNET_MASK : null;
    }

    /**
     * @private
     *
     * Converts an IPv4 or IPv6 address into a BigInteger. This is a workaround in two ways
     * because ip-address uses "jsbn" for their big int library and it's a joke. and/or/shifting
     * don't work right, so we have to pass it into BigInteger so we can actually do
     * bitwise logic on addresses. It also uses sprintf which doesn't work with IE11 because
     * it exports ES6 code.
     *
     * @param {Address4|Address6} addr
     * @return {BigInteger}
     */
    function _toBigInteger(addr) {
        return _.reduce(addr.toArray(), (ipInt, num) => ipInt.shiftLeft(8).or(num), BigInteger(0));
    }

    /**
     * Returns an IP address without a subnet
     *
     * @param {String|Address4|Address6} ip
     * @return {String}
     */
    function _getAddressWithoutSubnet(ip) {
        return parse(ip).addressMinusSuffix;
    }

    /**
     * Returns the subnet mask for a given IP address
     *
     * @param {String|Address4|Address6} ip
     * @return {Number}
     */
    function _getAddressSubnet(ip) {
        return parse(ip).subnetMask;
    }

    /**
     * Returns normalized IpAddress for a given IP address in order to sort
     *
     * @param {String|Address4|Address6} ip
     * @return {Object}
     */
    function normalizeIpAddress(ip) {
        if (iplib.IPv4.isValid(ip)) {
            const v4Address = iplib.parse(ip).toString();
            return `4_${v4Address
                .split('.')
                .map((i) => {
                    return `00${i}`.slice(-3);
                })
                .join('.')}`;
        }

        if (iplib.IPv6.isValid(ip)) {
            const v6Address = iplib.parse(ip).toNormalizedString();
            return `6_${v6Address
                .split(':')
                .map((i) => {
                    return `000${i}`.slice(-4);
                })
                .join(':')}`;
        }

        return '';
    }

    function formatIPAddress(value) {
        if (value) {
            const parsedIp = parse(value.trim());
            return parsedIp ? parsedIp.correctForm() : value;
        }

        return null;
    }

    function formatIPv6(ipv6) {
        if (ipv6 === '::' || ipv6 === '0:0:0:0:0:0:0:0' || !iplib.IPv6.isValid(ipv6)) {
            return null;
        }
        return ipv6;
    }

    function validateDomainName(domainName) {
        try {
            let DOMAIN_NAME_REGEX =
                /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/;

            // See if we are dealing with an IP
            if (isV4(domainName) || isV6(domainName)) {
                return true;
            }

            return DOMAIN_NAME_REGEX.test(domainName);
        } catch (e) {
            return false;
        }
    }
}

const IPAddressUtilities = ipAddressHelper();
export default IPAddressUtilities;
